import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useConfirm from '../hooks/useConfirm';
import ConfirmModal from './ConfirmModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const AuditLogs = () => {
    const [logs, setLogs] = useState([]);
    const [keyword, setKeyword] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const { confirm, confirmState, handleClose } = useConfirm();

    const fetchLogs = async () => {
        try {
            const res = await axios.get('/api/logs', {
                params: { keyword, startDate, endDate, timeZone: userTimeZone }
            });
            setLogs(res.data);
        } catch (err) { console.error("Log fetch error", err); }
    };

    const handleReset = () => {
        setKeyword('');
        setStartDate('');
        setEndDate('');
    };

    useEffect(() => {
        fetchLogs();
    }, [keyword, startDate, endDate]);

    const handleClearLogs = async () => {
        const ok = await confirm({
            title: 'Clear Activity Logs',
            message: 'Are you sure? This will delete all history. Download a CSV backup first!',
            confirmText: 'Clear Logs',
            cancelText: 'Cancel',
            danger: true
        });
        if (!ok) return;
        try {
            await axios.delete('/api/logs/clear');
            fetchLogs();
        } catch (err) { alert("Failed to clear logs."); }
    };

    const handleExportLogs = () => {
        window.open(`${API_BASE}/api/logs/export`, '_blank');
    };

    useEffect(() => { fetchLogs(); }, []);

    return (
        <div style={{ padding: '10px' }}>
            <h3 style={{ marginBottom: '15px' }}>System Activity Log</h3>
            <div style={filterContainerStyle}>
                <input type="text" placeholder="Search logs..." value={keyword} onChange={(e) => setKeyword(e.target.value)} style={searchInputStyle} />
                <label style={dateFilterStyle}>
                    <span style={dateFilterLabelStyle}>From date</span>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={searchInputStyle} />
                </label>
                <label style={dateFilterStyle}>
                    <span style={dateFilterLabelStyle}>To date</span>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={searchInputStyle} />
                </label>
                <button onClick={fetchLogs} style={searchBtnStyle}>Search</button>
                <button onClick={handleReset} style={clearFltrBtnStyle}>Clear Filters</button>
                <button onClick={handleExportLogs} style={exportBtnStyle}>Download CSV</button>
                <button onClick={handleClearLogs} style={clearBtnStyle}>Clear Activity Logs</button>
            </div>
            
            <div style={containerStyle}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                        <tr>
                            <th style={thStyle}>Action</th>
                            <th style={thStyle}>Performed By</th>
                            <th style={thStyle}>Details</th>
                            <th style={thStyle}>Timestamp</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.length > 0 ? logs.map((log) => (
                            <tr key={log.log_id} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={tdStyle}><span style={getBadgeStyle(log.action)}>{log.action}</span></td>
                                <td style={tdStyle}><strong>{log.full_name || 'System'}</strong></td>
                                <td style={tdStyle}>{log.details}</td>
                                <td style={tdStyle}>{new Date(log.created_at).toLocaleString()}</td>
                            </tr>
                        )) : (
                            <tr><td colSpan="4" style={{textAlign:'center', padding:'20px'}}>No records found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <ConfirmModal state={confirmState} onClose={handleClose} />
        </div>
    );
};

const filterContainerStyle = { background: '#f4f4f4', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' };
const searchInputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' };
const dateFilterStyle = { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', fontWeight: 'bold', color: '#555' };
const dateFilterLabelStyle = { lineHeight: 1 };
const containerStyle = { maxHeight: '500px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '8px' };
const thStyle = { padding: '12px', textAlign: 'left', fontSize: '13px', background: '#f8f9fa' };
const tdStyle = { padding: '12px', fontSize: '13px' };
const searchBtnStyle = { padding: '8px 15px', background: '#800000', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
const exportBtnStyle = { padding: '8px 15px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
const clearFltrBtnStyle = { padding: '8px 15px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
const clearBtnStyle = { padding: '8px 15px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };

const getBadgeStyle = (action) => {
    const a = String(action || '').toUpperCase();
    let bgColor = '#6c757d';

    if (a === 'UPLOAD' || a.includes('CREATED') || a.startsWith('CREATE_')) bgColor = '#28a745';
    else if (a === 'EDIT' || a.includes('UPDATED') || a.startsWith('DEPT_')) bgColor = '#007bff';
    else if (a.includes('DELETE') || a === 'DELETE' || a.includes('REJECT')) bgColor = '#dc3545';
    else if (a.includes('APPROVE') || a.includes('FILE_SENT')) bgColor = '#198754';
    else if (a.includes('REQUEST')) bgColor = '#6f42c1';
    else if (a.includes('PASSWORD') || a.includes('LOGIN') || a.includes('VERIFY')) bgColor = '#fd7e14';
    else if (a.includes('EXPORT') || a.includes('ASSIGN') || a.includes('SYSTEM')) bgColor = '#20c997';
    else if (a.includes('FILE')) bgColor = '#0d6efd';

    return { backgroundColor: bgColor, color: 'white', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' };
};

export default AuditLogs;

